import type { IngestOperatorMessage } from '@axilon-apps/domains-ontology-ui-state'

/**
 * Thread sort key for an operator note. Send time bookmarks the bubble after
 * whatever was already on screen; leftover CoT from the unread turn is clamped
 * just before this (see `clampAgentAtForOperatorMessages`).
 */
export function operatorMessageSortAt(m: IngestOperatorMessage): string {
  return m.sentAt || m.deliveredAt || ''
}

/**
 * Visible clock for an operator note. Send/queue time (`sentAt`) is what the
 * operator did; drain time is only used to close the leftover-CoT window.
 */
export function operatorMessageDisplayAt(m: IngestOperatorMessage): string {
  return m.sentAt || m.deliveredAt || ''
}

/**
 * Agent-log timestamps that fall in a delivered note's unread window (after
 * send, before the agent drained it) clamp to just before send. Leftover CoT
 * from the turn that was still running when the operator typed stays above the
 * note; the turn that actually read it (`created_at >= deliveredAt`) stays below.
 *
 * Still-queued notes have no closed window — they render after live CoT instead.
 */
export function clampAgentAtForOperatorMessages(
  at: string,
  messages: readonly IngestOperatorMessage[]
): string {
  let earliestSendMs: number | undefined
  for (const m of messages) {
    if (m.pending || !m.sentAt || !m.deliveredAt) continue
    if (at.localeCompare(m.sentAt) <= 0) continue
    if (at.localeCompare(m.deliveredAt) >= 0) continue
    const sentMs = Date.parse(m.sentAt)
    if (Number.isNaN(sentMs)) continue
    if (earliestSendMs === undefined || sentMs < earliestSendMs) {
      earliestSendMs = sentMs
    }
  }
  if (earliestSendMs === undefined) return at
  return new Date(earliestSendMs - 1).toISOString()
}
